import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';

const COOKIE_CONSENT_KEY = 'visto_cookie_consent';

type ConsentValue = 'accepted' | 'declined' | null;

// Pages where cookie consent should NOT appear (public feedback pages, celebration)
const EXCLUDED_PATHS = ['/s/', '/feedback/', '/wall/', '/celebration'];

export const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const location = useLocation();

  // Check if current path is excluded
  const isExcludedPath = EXCLUDED_PATHS.some(path => location.pathname.startsWith(path));

  useEffect(() => {
    // Don't show on excluded paths (public feedback pages)
    if (isExcludedPath) {
      setShowBanner(false);
      return;
    }

    // Check if user has already made a choice
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY) as ConsentValue;
    if (!consent) {
      // Small delay to avoid flash on page load
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [isExcludedPath]);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
    setShowBanner(false);

    // Enable Brevo tracking if available
    if (window.Brevo) {
      window.Brevo.push(['trackPageView']);
    }

    // Enable GTM/GA tracking
    if (window.dataLayer) {
      window.dataLayer.push({ event: 'cookie_consent_accepted' });
    }
  };

  const handleDecline = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'declined');
    setShowBanner(false);

    // Optionally disable non-essential cookies here
    if (window.dataLayer) {
      window.dataLayer.push({ event: 'cookie_consent_declined' });
    }
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-slide-up">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row gap-4 sm:items-start">
            {/* Icon */}
            <div className="hidden sm:flex w-12 h-12 bg-primary/10 rounded-xl items-center justify-center shrink-0">
              <Cookie className="w-6 h-6 text-primary" />
            </div>

            {/* Content */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2 sm:hidden">
                <Cookie className="w-5 h-5 text-primary" />
                <h3 className="font-semibold text-slate-900 dark:text-white">
                  Cookie Settings
                </h3>
              </div>
              <h3 className="hidden sm:block font-semibold text-slate-900 dark:text-white mb-1">
                We value your privacy
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                We use cookies to enhance your browsing experience, analyze site traffic, and personalize content.
                By clicking "Accept All", you consent to our use of cookies.{' '}
                <Link
                  to="/cookies"
                  className="text-primary hover:underline font-medium"
                >
                  Learn more
                </Link>
              </p>
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 sm:shrink-0">
              <button
                onClick={handleDecline}
                className="px-4 py-2.5 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                Decline
              </button>
              <button
                onClick={handleAccept}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors"
              >
                Accept All
              </button>
            </div>

            {/* Close button (mobile) */}
            <button
              onClick={handleDecline}
              className="absolute top-3 right-3 sm:hidden p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Type declarations for window globals
declare global {
  interface Window {
    Brevo?: any[];
    dataLayer?: any[];
  }
}
